import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '~/common/components/Button';
import CrudGrid from '~/common/components/CrudGrid';
import { OPERATORS } from '~/common/components/CrudGrid/Filters/filterGridData';
import { GridDef } from '~/common/components/CrudGrid/GridDef';
import {
  renderDate,
  renderNullValueAsDash,
} from '~/common/components/CrudGrid/crudGridUtils';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import Text from '~/common/components/Texts/Text';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import useUrl from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';
import { formatCurrency } from '~/common/util/format';

import ScrollIntoViewContainer from '../common/components/ScrollIntoViewContainer';
import { StyledLoan1003LayoutContainer } from '../common/components/StyledComponents';
import { withFeatureFlagBlock } from '../common/components/withFeatureFlagBlock';
import { formatName } from '../common/utils/utils';
import IncomeSummariesForm from './Income1003Form/Income1003Form';
import type { IncomeBorrower, IncomeBorrowerName, IncomeRow } from './types';

type ColumnDef<T = unknown> = {
  fieldName: keyof T | string;
  headerName: string;
  sortable?: boolean;
  isNumeric?: boolean;
  filterable?: boolean;
  renderCell?: (val: any) => React.ReactNode;
};

const columns: ColumnDef<IncomeRow>[] = [
  {
    fieldName: 'borrowerName',
    headerName: 'Borrower Name',
    sortable: false,
    filterable: true,
    renderCell: (val: IncomeBorrowerName) =>
      formatName(val.firstName, val.lastName, val.middleName, val.suffixName),
  },
  {
    fieldName: 'employerName',
    headerName: 'Employer or Description',
    sortable: false,
  },
  {
    fieldName: 'title',
    headerName: 'Job Title',
    sortable: false,

    renderCell: (val: string | undefined) => renderNullValueAsDash(val, '-'),
  },
  {
    fieldName: 'currentEmployment',
    headerName: 'Current',
    sortable: false,
    renderCell: (val: string) => (val === 'Y' ? 'Yes' : 'No'),
  },
  {
    fieldName: 'startDate',
    headerName: 'Start Date',
    sortable: true,
    renderCell: (val: string | null | undefined) => renderDate(val),
  },
  {
    fieldName: 'endDate',
    headerName: 'End Date',
    sortable: true,
    renderCell: (val: string | null | undefined) => renderDate(val),
  },
  {
    fieldName: 'monthlyAmount',
    headerName: 'Monthly Income',
    isNumeric: true,
    renderCell: (val: number) => formatCurrency(val),
  },
];

function evaluateBorrowerFilter(
  row: { customerRecordId: number },
  value: number[],
): boolean {
  return value.includes(row.customerRecordId);
}

const gridDef: GridDef = {
  defaultSort: { field: 'monthlyAmount', direction: 'desc' },
  uniqueIdAttributeFields: ['customerRecordId', 'employerId'],
  gridLanguage: {
    singularItem: 'Income',
  },
  getDialogDeleteMessage: (row) =>
    `Are you sure you want to remove income source -${row[0].employerName}`,
  readOnly: false,
  allowFilters: true,
};

const totalAmountStyle = {
  margin: '1rem 0rem',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  flexDirection: 'column' as const,
  width: '100%',
};

const Income1003 = withFeatureFlagBlock(
  'IncludeIncome',
  function Income1003Layout() {
    const dispatch = useDispatch();
    const [showForm, setShowForm] = useState(false);
    const [rowToEdit, setRowToEdit] = useState<IncomeRow | null>(null);
    const queryClient = useQueryClient();
    const { obfuscatedLoanIdentifier, isReadOnly } = useUrl();
    const showSnackbar = useSnackbar();

    const { data, isLoading, isError } = useQuery({
      queryKey: ['getSummaryIncomeData'],
      queryFn: async () => {
        try {
          const { data } =
            await typedApiClient.v1.underwritingSummaryIncomeGetUnderwritingSummaryIncomeData(
              {
                obfuscatedLoanIdentifier,
              },
            );

          return data;
        } catch (e) {
          showSnackbar({
            severity: 'error',
            message: 'Could not get data',
            duration: 5000,
          });

          throw e;
        }
      },
    });

    const { employmentReviewDetail, allBorrowers } = useMemo(
      () => ({
        employmentReviewDetail: data?.employmentReviewDetail ?? [],
        allBorrowers: data?.allBorrowers ?? [],
      }),
      [data],
    );

    // Use the type in your reduce:
    const totalMonthlyPayments = useMemo(() => {
      return employmentReviewDetail.reduce(
        (sum: number, obj) =>
          sum +
          (obj.currentEmployment.toString() === 'Y' ? obj.monthlyAmount : 0),
        0,
      );
    }, [employmentReviewDetail]);
    const isWriteAccessDisabled = useIsWriteAccessDisabled() || isReadOnly;

    const handleEditMode = useCallback((row: IncomeRow) => {
      setRowToEdit({
        ...row,
        customerRecordIdOriginal: row.customerRecordId,
      });
      setShowForm(true);
    }, []);

    const handleClose = useCallback(() => {
      setRowToEdit(null);
      setShowForm(false);
    }, []);

    const handleSave = useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['getSummaryIncomeData'] });
      setShowForm(false);
      setRowToEdit(null);
    }, [queryClient]);

    const { mutate: handleDelete } = useMutation({
      mutationFn: async (value: IncomeRow[]) => {
        const itemToDelete = value[0];

        const { data } =
          await typedApiClient.v1.underwritingSummaryIncomeDeleteUnderwritingSummaryIncome(
            {
              obfuscatedLoanIdentifier,
              customerIdentifier: {
                obfuscatedLenderDatabaseId:
                  obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
                customerRecordId: itemToDelete.customerRecordId,
              },
              employerId: itemToDelete.employerId,
            },
          );

        return data;
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: 'Record was not able to be deleted.',
          duration: 5000,
        });
      },
      onSuccess: () => {
        setRowToEdit(null);
        setShowForm(false);
        showSnackbar({
          severity: 'success',
          message: 'Employment Deleted',
          duration: 5000,
        });
        dispatch(setLoanSummaryUpsertOrDelete(true));
      },
    });

    const filters = useMemo(
      () => [
        {
          options: allBorrowers.map((borrower: IncomeBorrower) => ({
            value: borrower.customerRecordId,
            label: formatName(
              borrower.firstName,
              borrower.lastName,
              borrower.middleName,
              borrower.suffixName,
            ),
          })),
          label: 'Borrower(s)',
          fieldName: 'customerRecordId',
          operator: OPERATORS.EXCLUDE,
          evaluateFilter: evaluateBorrowerFilter,
        },
      ],
      [allBorrowers],
    );

    return (
      <StyledLoan1003LayoutContainer>
        <CrudGrid
          gridDef={gridDef}
          columns={columns}
          data={employmentReviewDetail}
          onRowEditClick={handleEditMode}
          filters={filters}
          isLoading={isLoading || isError}
          readOnly={isWriteAccessDisabled}
          selectedRow={rowToEdit}
          onDelete={handleDelete}
          actions={
            data?.hasWritePermission !== false && (
              <Button
                dataTestid="addIncomeButton"
                onClick={() => {
                  handleEditMode({
                    employerId: 0,
                    employerName: '',
                    borrowerName: {
                      firstName: '',
                      middleName: '',
                      lastName: '',
                      suffixName: '',
                    },
                    customerRecordId: null,
                    customerRecordIdOriginal: null,
                    currentEmployment: 'Y',
                    primaryEmployment: false,
                    country: null,
                    employerPhone: undefined,
                    endDate: null,
                    startDate: null,
                    yearsOnJob: null,
                    cpaInformation: [],
                    selfEmployed: false,
                    employerAddress: {
                      addressLine1: '',
                      state: '',
                      city: '',
                      zipCode: '',
                    },
                    monthlyIncomeDetails: [],
                    verifiedDate: null,
                  });
                }}
              >
                Add Income
              </Button>
            )
          }
        />
        {!isLoading && (
          <div style={totalAmountStyle}>
            <Text fontWeight={'bold'} margin={'0.75rem 0rem'}>
              Total Monthly Income : {formatCurrency(totalMonthlyPayments)}
            </Text>
          </div>
        )}
        {showForm && rowToEdit && (
          <ScrollIntoViewContainer valueToWatch={rowToEdit}>
            <IncomeSummariesForm
              key={`${rowToEdit?.customerRecordId}|${rowToEdit?.employerId}`}
              rowToEdit={rowToEdit}
              onClose={handleClose}
              onSave={handleSave}
              isWriteAccessDisabled={isWriteAccessDisabled}
              allBorrowerOptions={allBorrowers}
              obfuscatedIds={obfuscatedLoanIdentifier}
            />
          </ScrollIntoViewContainer>
        )}
      </StyledLoan1003LayoutContainer>
    );
  },
);

export default Income1003;
